import { Mark } from "./Mark";

/**
 * Where a run has got to, beside its name.
 *
 * **The mark moves only while somebody is waiting.** `Mark`'s own note draws
 * that line — a repeating animation is noise where nobody is — and a list of
 * engagements is the surface where it would be most tempting to break it: a
 * page of twenty finished runs, all of them drawing themselves forever. So
 * the loop is bound to the two states that are actually in flight, and every
 * terminal row gets the same mark standing still.
 *
 * Twenty pixels because nineteen is the floor: `Mark` drops to a coarser 3x3
 * below eighteen and that reduction never animates, so a smaller indicator
 * would silently stop moving rather than refuse.
 *
 * The word beside it is the status as the API spells it, not a re-wording —
 * `queued` and `rescheduling` mean something precise, and a friendlier
 * synonym here would disagree with the status column and with the run page.
 */
export function RunIndicator({
  status,
  phase,
}: {
  status: string;
  /** The phase now running, when there is one. Nothing on a finished run. */
  phase?: string | null;
}) {
  const live = status === "running" || status === "queued";
  return (
    <span className="inline-flex items-center gap-1.5 align-middle">
      <Mark size={20} animate={live} loop={live} />
      <span
        className={`text-xs ${live ? "text-ink-2" : "text-muted"}`}
        // The mark is decorative (`aria-hidden`), so this carries the state
        // for a screen reader. `status` keeps it from interrupting.
        role="status"
      >
        {status}
        {live && phase ? ` · ${phase}` : ""}
      </span>
    </span>
  );
}
